import React from 'react';
import { Table } from 'antd';
import {
  PAGE_SIZE_OPTIONS,
  SCROLL_Y,
  COLUMN_EXTRA_LARGE,
  COLUMN_LARGE,
} from 'constants/table';
import { checkValue, formatDate } from 'utils';
import { priceRender, renderWithTooltip, filterColumns } from 'utils/table';
import { employeesRecordsMap } from 'utils/handbooks/employeeHandbooks';
import { InputFilter } from 'components/Filters/InputFilter';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { SelectTableFilter } from 'components/Filters/SelectTableFilter';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';

function EmployeesRecordsTable({
  items,
  current,
  pageSize,
  loading,
  totalRows,
  filters = {},
  allowFilter = false,
  onRowClick,
  onChange,
  onFilter,
}) {
  const {
    full_name,
    iin,
    title,
    f_begin_date,
    t_begin_date,
    f_end_date,
    t_end_date,
    f_payment_date,
    t_payment_date,
  } = filters;

  const columns = [
    {
      width: COLUMN_LARGE,
      title: 'ФИО сотрудника',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (value) => renderWithTooltip(value),
      filterDropdown: () => (
        <InputFilter
          value={full_name}
          placeholder="Поиск по ФИО"
          onFilterChange={(full_name) => onFilter({ full_name })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={full_name} />,
    },
    {
      title: 'ИИН',
      dataIndex: 'iin',
      key: 'iin',
      render: (iin) => iin || '---',
      filterDropdown: () => (
        <InputFilter
          value={iin}
          placeholder="Поиск по ИИН"
          onFilterChange={(iin) => onFilter({ iin })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={iin} />,
    },
    {
      title: 'Наименование',
      dataIndex: 'record_type',
      key: 'record_type',
      render: (value) => employeesRecordsMap[value],
      filterDropdown: () => {
        return (
          <SelectTableFilter
            dataSourse={[
              { key: 'salary_change', text: 'Изменение ЗП' },
              { key: 'business_trip', text: 'Командировка' },
              { key: 'bonus', text: 'Премия' },
              { key: 'hospital', text: 'Больничные' },
              { key: 'compensation', text: 'Компенсация' },
              { key: 'paid_vacation', text: 'Отпуск оплачиваемый' },
              { key: 'leave_without_pay', text: 'Отпуск без содержания' },
              { key: 'review_vacation', text: 'Отзыв из отпуска' },
              { key: 'dismissal', text: 'Увольнение' },
            ]}
            itemKey="key"
            itemValue="text"
            value={title}
            placeholder="Выберите тип"
            getSelectedItem={(title) => onFilter({ title })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={title} />,
    },
    {
      title: 'Информация',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => renderWithTooltip(value),
    },
    {
      title: 'Сумма (тг)',
      dataIndex: 'amount',
      key: 'amount',
      render: priceRender,
    },
    {
      title: 'Дата начала',
      dataIndex: 'begin_date',
      key: 'begin_date',
      render: (date) => {
        if (date) return formatDate(date);
        return '---';
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_begin_date, t_begin_date]}
            onFilterChange={([start, end]) => {
              onFilter({ f_begin_date: start, t_begin_date: end });
            }}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_begin_date && t_begin_date} />,
    },
    {
      title: 'Дата окончания',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => {
        if (date) return formatDate(date);
        return '---';
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_end_date, t_end_date]}
            onFilterChange={([start, end]) => {
              onFilter({ f_end_date: start, t_end_date: end });
            }}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_end_date && t_end_date} />,
    },
    {
      title: 'Дата выплаты',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => {
        if (date) return formatDate(date);
        return '---';
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_payment_date, t_payment_date]}
            onFilterChange={([start, end]) => {
              onFilter({ f_payment_date: start, t_payment_date: end });
            }}
          />
        );
      },
      filterIcon: () => (
        <FilterIcon isActive={f_payment_date && t_payment_date} />
      ),
    },
    {
      align: 'center',
      title: 'Отпускные дни использованные',
      dataIndex: 'vacation_date_cnt',
      key: 'vacation_date_cnt',
      render: (value) => checkValue(value),
    },
    {
      width: COLUMN_EXTRA_LARGE,
      title: 'Клиент',
      dataIndex: 'company',
      key: 'company',
      render: (value) => renderWithTooltip(value, 60),
    },
  ];

  return (
    <Table
      columns={filterColumns(columns, allowFilter)}
      dataSource={items}
      loading={loading}
      scroll={{ x: 1600, y: SCROLL_Y }}
      pagination={{
        current,
        pageSize,
        total: totalRows,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      tableLayout="fixed"
      rowKey="id"
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }
        return null;
      }}
      onChange={onChange}
    />
  );
}

export { EmployeesRecordsTable };
